import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PD_BLUE = "#0037ff";
const PD_NIGHT = "#0a1230";
const HEX = /^#[0-9a-fA-F]{6}$/;
const bV = (c, fb) => (HEX.test(c || "") ? c : fb);

const isLight = (hex) => {
  const m = (hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(m)) return false;
  const r = parseInt(m.slice(0, 2), 16), g = parseInt(m.slice(2, 4), 16), b = parseInt(m.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62;
};

const shade = (hex, amt) => {
  const m = hex.replace("#", "");
  const f = (i) => Math.max(0, Math.min(255, Math.round(parseInt(m.slice(i, i + 2), 16) * (1 + amt))));
  return `#${[f(0), f(2), f(4)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

const UI = {
  es: { titulo: "Completa tu perfil de viajero", subtitulo: "Tus datos nos permiten preparar cada detalle de tu viaje. Solo te tomará unos minutos.", obligatorio: "Indica campo obligatorio", enviar: "Enviar registro", enviando: "Enviando…", requerido: "Este campo es obligatorio", consentReq: "Debes aceptar los consentimientos obligatorios", exito: "¡Registro recibido!", folioLbl: "Tu folio de confirmación", gracias: "Gracias por confiar en PD Travel", cerrado: "Este formulario ya no está disponible", noEncontrado: "Formulario no encontrado", archivo: "Arrastra tu archivo aquí o toca para seleccionar", archivoHint: "Imagen o PDF · máx. 10 MB", quitar: "Quitar", maxSize: "El archivo supera 10 MB", tipoArchivo: "Solo imagen o PDF", aviso: "Aviso de privacidad", errorEnvio: "No se pudo enviar. Revisa los campos e intenta de nuevo.", dudas: "¿Tienes dudas? Escríbenos:", progreso: "completado", autocolapsar: "Autocolapsar secciones", ladasFrecuentes: "Frecuentes", ladasMundo: "Resto del mundo", ladaNoLocalizada: "No localizada", pagoServicioIntroSing: "El servicio", pagoServicioCosto: "tiene un costo de", pagoServicioContacto: "Para hacer el pago de este servicio, nosotros te contactaremos en un periodo no mayor a", pagoServicioIntroMulti: "Estos son los servicios disponibles.", pagoServicioMarca: "Si requieres algún servicio, marca los que necesitas", pagoServicioSuma: "y se sumará el total. Te contactaremos en un periodo no mayor a", pagoServicioProcesar: "para procesar el pago.", pagoRequiereSing: "¿Requieres este servicio?", pagoRequiereMulti: "¿Requieres alguno de estos servicios?", pagoSiSing: "Sí, requiero este servicio", pagoSiMulti: "Sí, requiero uno o más", pagoNo: "No, no lo requiero", pagoSeleccionaLbl: "Selecciona los servicios que necesitas", pagoTotalLbl: "Total a pagar", pagoServicioWord: "servicio", pagoServiciosWord: "servicios", pagoMedioLbl: "¿Por qué medio deseas que te contactemos?", pagoMedioEmail: "Correo electrónico", pagoMedioWA: "WhatsApp", pagoUnidadHoras: "horas", pagoUnidadDias: "días", pagoUnidadSemanas: "semanas" },
  en: { titulo: "Complete your traveler profile", subtitulo: "Your information allows us to prepare every detail of your trip. It will only take a few minutes.", obligatorio: "Indicates required field", enviar: "Submit registration", enviando: "Submitting…", requerido: "This field is required", consentReq: "You must accept the required consents", exito: "Registration received!", folioLbl: "Your confirmation number", gracias: "Thank you for trusting PD Travel", cerrado: "This form is no longer available", noEncontrado: "Form not found", archivo: "Drag your file here or tap to select", archivoHint: "Image or PDF · max. 10 MB", quitar: "Remove", maxSize: "File exceeds 10 MB", tipoArchivo: "Only image or PDF", aviso: "Privacy notice", errorEnvio: "Could not submit. Please review the fields and try again.", dudas: "Questions? Write to us:", progreso: "completed", autocolapsar: "Auto-collapse sections", ladasFrecuentes: "Frequent", ladasMundo: "Rest of the world", ladaNoLocalizada: "Not listed", pagoServicioIntroSing: "The", pagoServicioCosto: "service has a cost of", pagoServicioContacto: "To pay for this service, we will contact you within no more than", pagoServicioIntroMulti: "These are the available services.", pagoServicioMarca: "If you need any service, check the ones you need", pagoServicioSuma: "and the total will be added. We will contact you within no more than", pagoServicioProcesar: "to process the payment.", pagoRequiereSing: "Do you need this service?", pagoRequiereMulti: "Do you need any of these services?", pagoSiSing: "Yes, I need this service", pagoSiMulti: "Yes, I need one or more", pagoNo: "No, I don't need it", pagoSeleccionaLbl: "Select the services you need", pagoTotalLbl: "Total to pay", pagoServicioWord: "service", pagoServiciosWord: "services", pagoMedioLbl: "How would you like us to contact you?", pagoMedioEmail: "Email", pagoMedioWA: "WhatsApp", pagoUnidadHoras: "hours", pagoUnidadDias: "days", pagoUnidadSemanas: "weeks" },
  fr: { titulo: "Complétez votre profil de voyageur", subtitulo: "Vos informations nous permettent de préparer chaque détail de votre voyage. Cela ne prendra que quelques minutes.", obligatorio: "Champ obligatoire", enviar: "Envoyer l'inscription", enviando: "Envoi…", requerido: "Ce champ est obligatoire", consentReq: "Vous devez accepter les consentements obligatoires", exito: "Inscription reçue !", folioLbl: "Votre numéro de confirmation", gracias: "Merci de faire confiance à PD Travel", cerrado: "Ce formulaire n'est plus disponible", noEncontrado: "Formulaire introuvable", archivo: "Glissez votre fichier ici ou touchez pour sélectionner", archivoHint: "Image ou PDF · max. 10 Mo", quitar: "Retirer", maxSize: "Le fichier dépasse 10 Mo", tipoArchivo: "Image ou PDF uniquement", aviso: "Avis de confidentialité", errorEnvio: "Échec de l'envoi. Vérifiez les champs et réessayez.", dudas: "Des questions ? Écrivez-nous :", progreso: "complété", autocolapsar: "Réduire les sections auto", ladasFrecuentes: "Fréquents", ladasMundo: "Reste du monde", ladaNoLocalizada: "Non répertorié", pagoServicioIntroSing: "Le service", pagoServicioCosto: "a un coût de", pagoServicioContacto: "Pour effectuer le paiement de ce service, nous vous contacterons dans un délai maximum de", pagoServicioIntroMulti: "Voici les services disponibles.", pagoServicioMarca: "Si vous avez besoin d'un service, cochez ceux dont vous avez besoin", pagoServicioSuma: "et le total sera ajouté. Nous vous contacterons dans un délai maximum de", pagoServicioProcesar: "pour traiter le paiement.", pagoRequiereSing: "Avez-vous besoin de ce service ?", pagoRequiereMulti: "Avez-vous besoin de l'un de ces services ?", pagoSiSing: "Oui, j'ai besoin de ce service", pagoSiMulti: "Oui, j'en ai besoin d'un ou plusieurs", pagoNo: "Non, je n'en ai pas besoin", pagoSeleccionaLbl: "Sélectionnez les services dont vous avez besoin", pagoTotalLbl: "Total à payer", pagoServicioWord: "service", pagoServiciosWord: "services", pagoMedioLbl: "Par quel moyen souhaitez-vous que nous vous contactions ?", pagoMedioEmail: "E-mail", pagoMedioWA: "WhatsApp", pagoUnidadHoras: "heures", pagoUnidadDias: "jours", pagoUnidadSemanas: "semaines" },
};

const TOP_LADAS = [
  ["MX", "+52"], ["US", "+1"], ["CA", "+1"], ["ES", "+34"], ["FR", "+33"], ["GB", "+44"],
  ["DE", "+49"], ["IT", "+39"], ["PT", "+351"], ["NL", "+31"], ["BR", "+55"], ["AR", "+54"],
  ["CO", "+57"], ["CL", "+56"], ["PE", "+51"], ["CR", "+506"], ["PA", "+507"], ["JP", "+81"],
  ["CN", "+86"], ["AE", "+971"],
];

const WORLD_LADAS = [
  ["AF", "+93"], ["AL", "+355"], ["DZ", "+213"], ["AD", "+376"], ["AO", "+244"], ["AI", "+1264"],
  ["AG", "+1268"], ["AM", "+374"], ["AW", "+297"], ["AU", "+61"], ["AT", "+43"], ["AZ", "+994"],
  ["BS", "+1242"], ["BD", "+880"], ["BB", "+1246"], ["BH", "+973"], ["BE", "+32"], ["BZ", "+501"],
  ["BJ", "+229"], ["BM", "+1441"], ["BY", "+375"], ["BO", "+591"], ["BA", "+387"], ["BW", "+267"],
  ["BN", "+673"], ["BG", "+359"], ["BF", "+226"], ["BI", "+257"], ["BT", "+975"], ["CV", "+238"],
  ["KH", "+855"], ["CM", "+237"], ["QA", "+974"], ["TD", "+235"], ["CY", "+357"], ["KM", "+269"],
  ["CG", "+242"], ["CD", "+243"], ["KR", "+82"], ["CI", "+225"], ["HR", "+385"], ["CU", "+53"],
  ["CW", "+599"], ["DK", "+45"], ["DJ", "+253"], ["DM", "+1767"], ["DO", "+1809"], ["EC", "+593"],
  ["EG", "+20"], ["SV", "+503"], ["ER", "+291"], ["SK", "+421"], ["SI", "+386"], ["EE", "+372"],
  ["ET", "+251"], ["PH", "+63"], ["FI", "+358"], ["FJ", "+679"], ["GA", "+241"], ["GM", "+220"],
  ["GE", "+995"], ["GH", "+233"], ["GI", "+350"], ["GD", "+1473"], ["GR", "+30"], ["GL", "+299"],
  ["GT", "+502"], ["GN", "+224"], ["GW", "+245"], ["GQ", "+240"], ["GY", "+592"], ["HT", "+509"],
  ["HN", "+504"], ["HK", "+852"], ["HU", "+36"], ["IN", "+91"], ["ID", "+62"], ["IQ", "+964"],
  ["IR", "+98"], ["IE", "+353"], ["IS", "+354"], ["KY", "+1345"], ["IL", "+972"], ["JM", "+1876"],
  ["JO", "+962"], ["KZ", "+7"], ["KE", "+254"], ["KG", "+996"], ["KW", "+965"], ["LA", "+856"],
  ["LS", "+266"], ["LV", "+371"], ["LB", "+961"], ["LR", "+231"], ["LY", "+218"], ["LI", "+423"],
  ["LT", "+370"], ["LU", "+352"], ["MO", "+853"], ["MK", "+389"], ["MG", "+261"], ["MY", "+60"],
  ["MW", "+265"], ["MV", "+960"], ["ML", "+223"], ["MT", "+356"], ["MA", "+212"], ["MQ", "+596"],
  ["MU", "+230"], ["MR", "+222"], ["MD", "+373"], ["MC", "+377"], ["MN", "+976"], ["ME", "+382"],
  ["MZ", "+258"], ["MM", "+95"], ["NA", "+264"], ["NP", "+977"], ["NI", "+505"], ["NE", "+227"],
  ["NG", "+234"], ["NO", "+47"], ["NC", "+687"], ["NZ", "+64"], ["OM", "+968"], ["PK", "+92"],
  ["PG", "+675"], ["PY", "+595"], ["PF", "+689"], ["PL", "+48"], ["PR", "+1787"], ["CF", "+236"],
  ["CZ", "+420"], ["RE", "+262"], ["RW", "+250"], ["RO", "+40"], ["RU", "+7"], ["WS", "+685"],
  ["KN", "+1869"], ["SM", "+378"], ["VC", "+1784"], ["LC", "+1758"], ["ST", "+239"], ["SN", "+221"],
  ["RS", "+381"], ["SC", "+248"], ["SL", "+232"], ["SG", "+65"], ["SO", "+252"], ["LK", "+94"],
  ["SZ", "+268"], ["ZA", "+27"], ["SD", "+249"], ["SS", "+211"], ["SE", "+46"], ["CH", "+41"],
  ["SR", "+597"], ["TH", "+66"], ["TW", "+886"], ["TZ", "+255"], ["TJ", "+992"], ["TL", "+670"],
  ["TG", "+228"], ["TO", "+676"], ["TT", "+1868"], ["TN", "+216"], ["TM", "+993"], ["TR", "+90"],
  ["UA", "+380"], ["UG", "+256"], ["UY", "+598"], ["UZ", "+998"], ["VU", "+678"], ["VE", "+58"],
  ["VN", "+84"], ["YE", "+967"], ["ZM", "+260"], ["ZW", "+263"],
].sort((a, b) => parseInt(a[1].slice(1), 10) - parseInt(b[1].slice(1), 10));

const flagOf = (iso) => iso.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
const ladaKey = ([iso, code]) => `${code}#${iso}`;
const NO_LADA = "?";

const inputCls = "w-full border border-slate-200 rounded-xl px-3.5 py-3 text-[15px] bg-slate-50/60 transition-all duration-200 focus:outline-none focus:bg-white focus:border-[color:var(--pv-base)] focus:ring-4 focus:ring-[color:var(--pv-ring)] hover:border-slate-300";

const PhoneField = ({ value, onChange, lang, testid }) => {
  const t = UI[lang];
  const [key, setKey] = useState(ladaKey(TOP_LADAS[0]));
  const [num, setNum] = useState("");
  useEffect(() => {
    if (value) {
      const m = value.match(/^(\+\d+)\s(.*)$/);
      if (m) {
        const entry = TOP_LADAS.find((e) => e[1] === m[1]) || WORLD_LADAS.find((e) => e[1] === m[1]);
        setKey(entry ? ladaKey(entry) : NO_LADA);
        setNum(m[2]);
      } else {
        setKey(NO_LADA);
        setNum(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const emit = (k, n) => {
    const code = k === NO_LADA ? "" : k.split("#")[0];
    onChange(n ? (code ? `${code} ${n}` : n) : "");
  };
  return (
    <div className="flex gap-2">
      <select className="border border-slate-200 rounded-xl px-2 py-3 text-[15px] bg-slate-50/60 transition-all duration-200 focus:outline-none focus:bg-white focus:border-[color:var(--pv-base)] focus:ring-4 focus:ring-[color:var(--pv-ring)] hover:border-slate-300 flex-shrink-0" style={{ width: 118 }} value={key} onChange={(e) => { setKey(e.target.value); emit(e.target.value, num); }} data-testid={`${testid}-lada`}>
        <optgroup label={t.ladasFrecuentes}>
          {TOP_LADAS.map((e) => <option key={ladaKey(e)} value={ladaKey(e)}>{flagOf(e[0])} {e[1]}</option>)}
        </optgroup>
        <optgroup label={t.ladasMundo}>
          {WORLD_LADAS.map((e) => <option key={ladaKey(e)} value={ladaKey(e)}>{flagOf(e[0])} {e[1]}</option>)}
        </optgroup>
        <option value={NO_LADA}>🌐 {t.ladaNoLocalizada}</option>
      </select>
      <input className={inputCls} inputMode="tel" value={num} placeholder="5512345678"
        onChange={(e) => { const n = e.target.value.replace(/[^\d\s]/g, ""); setNum(n); emit(key, n); }} data-testid={testid} />
    </div>
  );
};

const FileField = ({ file, onFile, lang, testid }) => {
  const t = UI[lang];
  const [err, setErr] = useState("");
  const handle = (f) => {
    if (!f) return;
    const ok = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!ok) return setErr(t.tipoArchivo);
    if (f.size > 10 * 1024 * 1024) return setErr(t.maxSize);
    setErr("");
    onFile(f);
  };
  return (
    <div>
      {file ? (
        <div className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 bg-slate-50">
          <span className="text-sm text-slate-700 truncate flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" style={{ color: "var(--pv-base)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
            {file.name}
          </span>
          <button type="button" className="text-xs text-red-500 font-bold ml-3 flex-shrink-0" onClick={() => onFile(null)} data-testid={`${testid}-remove`}>{t.quitar}</button>
        </div>
      ) : (
        <label
          className="group block border-2 border-dashed border-slate-200 rounded-xl px-3 py-6 text-center cursor-pointer transition-all duration-200 hover:border-[color:var(--pv-base)] hover:bg-[color:var(--pv-tint)]"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handle(e.dataTransfer.files?.[0]); }}
          data-testid={`${testid}-drop`}
        >
          <svg className="w-7 h-7 mx-auto mb-1.5 text-slate-300 group-hover:text-[color:var(--pv-base)] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
          <span className="text-sm text-slate-500 font-medium">{t.archivo}</span>
          <span className="block text-[11px] text-slate-400 mt-0.5">{t.archivoHint}</span>
          <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => handle(e.target.files?.[0])} data-testid={testid} />
        </label>
      )}
      {err && <p className="text-xs text-red-500 mt-1">{err}</p>}
    </div>
  );
};

const PDFooter = ({ headerColor, lang }) => {
  const light = isLight(headerColor);
  return (
    <footer className="mt-auto py-8 px-4 text-center" style={{ background: headerColor }}>
      <p className="text-[10px] tracking-[0.35em] uppercase font-semibold" style={{ color: light ? "#47556999" : "rgba(255,255,255,0.5)" }}>Powered by</p>
      <img src={light ? "/pd-logo-blue.png" : "/pd-logo-white.png"} alt="PD Travel" className="h-8 mx-auto mt-2.5 object-contain" />
      <p className="text-[11px] mt-3" style={{ color: light ? "#47556999" : "rgba(255,255,255,0.45)" }}>Expertos en viajes y eventos de negocios.</p>
      <p className="text-[11px] mt-1" style={{ color: light ? "#475569b3" : "rgba(255,255,255,0.55)" }}>
        {UI[lang].dudas} <a href="mailto:hola@pdtravelmex.com" className="underline font-semibold">hola@pdtravelmex.com</a>
      </p>
    </footer>
  );
};

const ANIM_CSS = `
@keyframes pvFadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
@keyframes pvPop { 0% { transform: scale(0); } 70% { transform: scale(1.15); } 100% { transform: scale(1); } }
.pv-fade { animation: pvFadeUp .55s cubic-bezier(.22,.9,.35,1) both; }
.pv-pop { animation: pvPop .5s cubic-bezier(.22,.9,.35,1) .15s both; }
.pv-root h1, .pv-root h2, .pv-root h3 { font-family: 'Mulish', sans-serif; letter-spacing: -0.01em; }
/* Estilos para descripciones ricas de servicios (Tiptap → HTML) */
.pv-rich { line-height: 1.5; }
.pv-rich p { margin: 0.35em 0; }
.pv-rich p:first-child { margin-top: 0; }
.pv-rich p:last-child { margin-bottom: 0; }
.pv-rich ul { list-style: disc; padding-left: 1.25em; margin: 0.35em 0; }
.pv-rich ol { list-style: decimal; padding-left: 1.5em; margin: 0.35em 0; }
.pv-rich li { margin: 0.15em 0; }
.pv-rich strong, .pv-rich b { font-weight: 700; color: #0f172a; }
.pv-rich em, .pv-rich i { font-style: italic; }
.pv-rich u { text-decoration: underline; }
.pv-rich h1 { font-size: 1.15em; font-weight: 700; margin: 0.4em 0 0.2em; }
.pv-rich h2 { font-size: 1.08em; font-weight: 700; margin: 0.4em 0 0.2em; }
.pv-rich h3 { font-size: 1em; font-weight: 700; margin: 0.4em 0 0.2em; }
.pv-rich a { color: #0037ff; text-decoration: underline; }
.pv-rich blockquote { border-left: 3px solid #cbd5e1; padding-left: 0.75em; color: #475569; margin: 0.4em 0; }
.pv-rich mark { background: #fef08a; padding: 0 2px; border-radius: 2px; }
.pv-rich code { background: #f1f5f9; padding: 0 4px; border-radius: 3px; font-family: monospace; font-size: 0.9em; }
`;

export default function ViajeroExternoPublico() {
  const { slug, subslug } = useParams();
  const publicPath = subslug ? `${slug}/${subslug}` : slug;
  const [cfg, setCfg] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | cerrado | 404
  const [lang, setLang] = useState("es");
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [autoCollapse, setAutoCollapse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [folio, setFolio] = useState(null);
  const [pagoSrv, setPagoSrv] = useState({
    requiere: null,             // null | true | false
    servicios_seleccionados: [], // array de IDs cuando hay >1 servicio
    medios: [],                  // ["correo","whatsapp"]
  });
  const [pagoErrors, setPagoErrors] = useState({});
  const t = UI[lang];

  useEffect(() => {
    axios.get(`${API}/viajeros-ext/public/${publicPath}`)
      .then((res) => {
        if (!res.data.activo) return setState("cerrado");
        setCfg(res.data);
        setState("ok");
        const key = `pv_visit_${publicPath}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          axios.post(`${API}/viajeros-ext/public/${publicPath}/visit`).catch(() => {});
        }
      })
      .catch(() => setState("404"));
  }, [publicPath]);

  const langs = useMemo(() => {
    const l = ["es"];
    if (cfg?.idiomas?.en) l.push("en");
    if (cfg?.idiomas?.fr) l.push("fr");
    return l;
  }, [cfg]);

  const isFilled = (f) => (f.t === "file" ? !!files[f.k] : f.t === "check" ? values[f.k] === "Sí" : !!(values[f.k] || "").trim());

  const allFields = useMemo(() => {
    if (!cfg) return [];
    return cfg.sections.flatMap((s) => s.fields);
  }, [cfg]);

  const progress = useMemo(() => {
    if (!allFields.length) return 0;
    return Math.round((allFields.filter(isFilled).length / allFields.length) * 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFields, values, files]);

  const focusSection = (si) => {
    if (!autoCollapse) return;
    setCollapsed((prev) => {
      const next = { ...prev };
      cfg.sections.forEach((s, i) => { if (i < si) next[s.key] = true; });
      next[cfg.sections[si].key] = false;
      return next;
    });
  };

  const toggleSection = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const set = (k, v) => { setValues((s) => ({ ...s, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const submit = async () => {
    const errs = {};
    for (const s of cfg.sections) {
      for (const f of s.fields) {
        if (!f.req) continue;
        if (f.t === "file") { if (!files[f.k]) errs[f.k] = t.requerido; }
        else if (f.t === "check") { if (values[f.k] !== "Sí") errs[f.k] = t.consentReq; }
        else if (!(values[f.k] || "").trim()) errs[f.k] = t.requerido;
      }
    }
    // Validar Pago de Servicios (si el proyecto lo tiene activado)
    const psc = cfg?.pago_servicios_config;
    const pagoEnabled = !!psc?.enabled;
    const servicios = psc?.servicios || [];
    const pErrs = {};
    if (pagoEnabled) {
      if (pagoSrv.requiere === null) {
        pErrs.requiere = t.requerido;
      } else if (pagoSrv.requiere === true) {
        if (servicios.length > 1 && !pagoSrv.servicios_seleccionados.length) {
          pErrs.servicios = t.requerido;
        }
        if (!pagoSrv.medios.length) pErrs.medios = t.requerido;
      }
    }
    setErrors(errs);
    setPagoErrors(pErrs);
    if (Object.keys(errs).length || Object.keys(pErrs).length) {
      setCollapsed((prev) => {
        const next = { ...prev };
        cfg.sections.forEach((s) => { if (s.fields.some((f) => errs[f.k])) next[s.key] = false; });
        return next;
      });
      const firstKey = Object.keys(errs)[0] || (Object.keys(pErrs).length ? `pago_${Object.keys(pErrs)[0]}` : null);
      if (firstKey) setTimeout(() => document.querySelector(`[data-field="${firstKey}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("website", "");
      const datos = { ...values };
      Object.keys(files).forEach((k) => { if (files[k]) fd.append(k, files[k]); });
      const payload = { idioma: lang, datos };
      if (pagoEnabled && pagoSrv.requiere !== null) {
        // Si hay 1 servicio, se toma automáticamente. Si hay más, se envían los IDs seleccionados.
        const selIds = servicios.length === 1 ? [servicios[0].id] : pagoSrv.servicios_seleccionados;
        payload.pago_servicios = {
          requiere: pagoSrv.requiere,
          servicios_seleccionados: selIds,
          medios_contacto: pagoSrv.medios,
        };
      }
      fd.append("payload", JSON.stringify(payload));
      const res = await axios.post(`${API}/viajeros-ext/public/${publicPath}/submit`, fd);
      setFolio(res.data.folio);
      window.scrollTo({ top: 0 });
    } catch (err) {
      alert(err.response?.data?.detail || t.errorEnvio);
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center text-slate-400">…</div>;
  if (state === "404" || state === "cerrado") return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: PD_NIGHT, fontFamily: "'Mulish', sans-serif" }}>
      <div className="text-center" data-testid="form-cerrado">
        <img src="/pd-logo-white.png" alt="PD Travel" className="h-10 mx-auto mb-6 object-contain" />
        <p className="text-blue-200 text-lg">{state === "cerrado" ? UI.es.cerrado : UI.es.noEncontrado}</p>
      </div>
    </div>
  );

  const b = cfg.branding || {};
  const brandColor = bV(b.color_base, PD_BLUE);
  const accentColor = bV(b.color_acento, brandColor);
  const headerColor = bV(b.color_header, bV(b.color_dark, PD_NIGHT));
  const suaveColor = bV(b.color_suave, "#f4f6fb");
  const headerLight = isLight(headerColor);
  const logoEmpresa = b.logo_empresa_id ? `${API}/viajeros-ext/logos/${b.logo_empresa_id}` : (b.logo_cliente_url || null);
  const logoEvento = b.logo_evento_id ? `${API}/viajeros-ext/logos/${b.logo_evento_id}` : null;

  if (folio) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 pv-root" style={{ background: PD_NIGHT, fontFamily: "'Mulish', sans-serif" }} data-testid="pantalla-exito">
      <style>{ANIM_CSS}</style>
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-10 text-center pv-fade">
        {logoEvento && <img src={logoEvento} alt="" className="h-16 mx-auto mb-5 object-contain" />}
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white text-3xl mb-5 pv-pop shadow-lg" style={{ background: PD_BLUE, boxShadow: `0 12px 28px -8px ${PD_BLUE}80` }}>✓</div>
        <h1 className="text-2xl font-extrabold mb-2" style={{ color: PD_NIGHT }}>{t.exito}</h1>
        <p className="text-slate-500 text-sm mb-4">{t.folioLbl}</p>
        <p className="text-5xl font-extrabold tracking-wider mb-6" style={{ color: PD_BLUE }} data-testid="folio-exito">{folio}</p>
        <p className="font-bold text-lg" style={{ color: PD_NIGHT }}>{t.gracias}</p>
        <div className="border-t border-slate-100 mt-7 pt-5">
          <p className="text-[9px] tracking-[0.35em] uppercase text-slate-400 font-semibold">Powered by</p>
          <img src="/pd-logo-blue.png" alt="PD Travel" className="h-6 mx-auto mt-2 object-contain" />
        </div>
      </div>
    </div>
  );

  let secNum = 0;

  return (
    <div className="min-h-screen flex flex-col pv-root" data-testid="viajero-ext-form"
      style={{ fontFamily: "'Mulish', sans-serif", background: suaveColor, "--pv-base": brandColor, "--pv-ring": `${brandColor}26`, "--pv-tint": `${brandColor}0D` }}>
      <style>{ANIM_CSS}</style>

      {/* Barra de progreso */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-black/10">
        <div className="h-full transition-all duration-500 ease-out" style={{ width: `${progress}%`, background: accentColor }} data-testid="progress-bar" />
      </div>

      {/* Header */}
      <header className="relative overflow-hidden" style={{ background: `linear-gradient(130deg, ${shade(headerColor, 0.10)} 0%, ${headerColor} 55%, ${shade(headerColor, -0.18)} 100%)` }}>
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.12] blur-3xl pointer-events-none" style={{ background: "#ffffff" }} />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: "#ffffff" }} />
        <div className="max-w-2xl mx-auto px-5 pt-7 pb-24 relative flex items-center justify-between gap-4">
          {logoEmpresa
            ? <img src={logoEmpresa} alt="" className="h-12 max-w-[200px] object-contain" data-testid="logo-empresa" />
            : <img src={headerLight ? "/pd-logo-blue.png" : "/pd-logo-white.png"} alt="PD Travel" className="h-8 object-contain" data-testid="logo-empresa-fallback" />}
          {langs.length > 1 && (
            <div className="flex gap-1 flex-shrink-0" data-testid="lang-selector">
              {langs.map((l) => (
                <button key={l} onClick={() => setLang(l)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase transition-all duration-200 ${lang === l ? "bg-white shadow-md" : headerLight ? "text-slate-600 border border-slate-400/40 hover:bg-white/40" : "text-white/70 border border-white/30 hover:bg-white/10"}`}
                  style={lang === l ? { color: brandColor } : {}} data-testid={`lang-${l}`}>
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Hero superpuesto */}
      <div className="max-w-2xl mx-auto px-4 -mt-16 w-full relative z-10">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 px-6 sm:px-10 py-9 text-center pv-fade">
          {logoEvento && <img src={logoEvento} alt="" className="h-20 sm:h-24 mx-auto mb-5 object-contain" data-testid="logo-evento" />}
          <span className="inline-block px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-[0.12em] uppercase" style={{ background: `${brandColor}14`, color: brandColor }} data-testid="header-titulo-publico">
            {cfg.titulo_publico_activo && (cfg.titulo_publico || "").trim()
              ? cfg.titulo_publico
              : [cfg.cliente_nombre, cfg.nombre].filter(Boolean).join(" | ")}
          </span>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-4 leading-tight" style={{ fontFamily: "'Mulish', sans-serif" }}>{t.titulo}</h1>
          <p className="text-slate-500 text-sm sm:text-[15px] mt-3 max-w-lg mx-auto leading-relaxed">
            {cfg.mensaje_bienvenida?.[lang] || cfg.mensaje_bienvenida?.es || t.subtitulo}
          </p>
          <p className="text-[11px] text-slate-400 mt-4"><span className="text-red-400">*</span> {t.obligatorio}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 pb-12 w-full flex-1">
        <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" value="" readOnly />

        {/* Barra de avance (campos activos del formulario) */}
        <div className="sticky top-3 z-40 mb-5 flex justify-center items-center gap-2 flex-wrap">
          <div className="bg-white/95 backdrop-blur rounded-full shadow-lg border border-slate-100 px-4 py-2 flex items-center gap-3" data-testid="progress-pill">
            <div className="w-28 sm:w-36 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%`, background: accentColor }} />
            </div>
            <span className="text-sm font-black text-slate-900 whitespace-nowrap" data-testid="progress-percent">
              {progress}% <span className="text-xs font-semibold text-slate-400">{t.progreso}</span>
            </span>
          </div>
          <label className="bg-white/95 backdrop-blur rounded-full shadow-lg border border-slate-100 px-3.5 py-2 flex items-center gap-2 cursor-pointer select-none" data-testid="auto-collapse-pill">
            <input type="checkbox" className="w-3.5 h-3.5" style={{ accentColor: accentColor }}
              checked={autoCollapse} onChange={(e) => setAutoCollapse(e.target.checked)} data-testid="auto-collapse-check" />
            <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">{t.autocolapsar}</span>
          </label>
        </div>

        {(() => {
          // Insertar la sección "Pago de Servicios" al 'posicion' entre las secciones.
          const psc = cfg.pago_servicios_config;
          const list = [...cfg.sections.map((s) => ({ kind: "section", data: s }))];
          if (psc?.enabled) {
            const idx = Math.max(0, Math.min((psc.posicion || 999) - 1, list.length));
            list.splice(idx, 0, { kind: "pago", data: psc });
          }
          return list.map((item, si) => {
            secNum += 1;
            if (item.kind === "pago") {
              const psc2 = item.data;
              const titulo = (lang === "en" && psc2.titulo_en) ? psc2.titulo_en : (lang === "fr" && psc2.titulo_fr) ? psc2.titulo_fr : (psc2.titulo || "Pago de Servicios");
              // Textos configurables de botones y mensaje "No" (por idioma con fallback ES → traducción por defecto)
              const trTop = (base) => {
                const key = base + (lang === "en" ? "_en" : lang === "fr" ? "_fr" : "");
                return (lang === "es" ? (psc2[base] || "") : (psc2[key] || psc2[base] || ""));
              };
              const cfgBotonSi = trTop("boton_si");
              const cfgBotonNo = trTop("boton_no");
              const cfgTextoNo = trTop("texto_no");
              // Aplicar traducciones a servicios según lang
              const trField = (s, base) => {
                const key = base + (lang === "en" ? "_en" : lang === "fr" ? "_fr" : "");
                return (lang === "es" ? s[base] : (s[key] || s[base]));
              };
              const srvListRaw = psc2.servicios || [];
              const srvList = srvListRaw.map((s) => ({ ...s, nombre: trField(s, "nombre"), descripcion: trField(s, "descripcion") }));
              const multi = srvList.length > 1;
              const selected = multi ? pagoSrv.servicios_seleccionados : (srvList.length === 1 ? [srvList[0].id] : []);
              const selectedSet = new Set(selected);
              const total = srvList.filter((s) => selectedSet.has(s.id)).reduce((acc, s) => acc + (s.costo || 0), 0);
              const filledCount = (pagoSrv.requiere === null ? 0 : (pagoSrv.requiere === false ? 1 : (1 + (multi ? (pagoSrv.servicios_seleccionados.length ? 1 : 0) : 0) + (pagoSrv.medios.length ? 1 : 0))));
              const totalCount = pagoSrv.requiere === true ? (multi ? 3 : 2) : 1;
              const toggleServicio = (id) => {
                setPagoSrv((s) => ({
                  ...s,
                  servicios_seleccionados: s.servicios_seleccionados.includes(id)
                    ? s.servicios_seleccionados.filter((x) => x !== id)
                    : [...s.servicios_seleccionados, id],
                }));
              };
              return (
                <div key="__pago__" className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-5 overflow-hidden pv-fade transition-shadow duration-300 hover:shadow-md"
                  style={{ animationDelay: `${Math.min(si * 70, 400)}ms` }} data-testid="seccion-pago-servicios">
                  <div className="w-full px-5 sm:px-6 py-4 flex items-center gap-3.5 text-left"
                    style={{ background: `linear-gradient(90deg, ${brandColor}08, transparent 60%)`, borderBottom: "1px solid #f8fafc" }}>
                    <span className="w-8 h-8 rounded-xl text-white text-sm font-extrabold flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: brandColor, boxShadow: `0 4px 10px -3px ${brandColor}66` }}>{secNum}</span>
                    <h2 className="font-extrabold text-slate-800 text-[15px] sm:text-base flex-1" style={{ fontFamily: "'Mulish', sans-serif" }}>{titulo}</h2>
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                      style={filledCount === totalCount ? { background: "#dcfce7", color: "#15803d" } : { background: `${brandColor}12`, color: brandColor }}>
                      {filledCount}/{totalCount}
                    </span>
                  </div>
                  <div className="p-5 sm:p-6 space-y-4">
                    {/* Info del servicio (visible por defecto y cuando responde SÍ) */}
                    {srvList.length > 0 && pagoSrv.requiere !== false && (
                      <div className="rounded-xl p-4 border-2 border-dashed space-y-2" style={{ borderColor: accentColor, background: `${accentColor}0d` }} data-testid="pago-info-servicio">
                        {srvList.length === 1 ? (
                          <p className="text-sm text-slate-800 leading-relaxed">
                            {t.pagoServicioIntroSing} <b>{srvList[0].nombre}</b> {t.pagoServicioCosto}{" "}
                            <b style={{ color: accentColor }}>${Number(srvList[0].costo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</b>.
                            {srvList[0].descripcion && <div className="text-xs text-slate-600 mt-1 pv-rich" dangerouslySetInnerHTML={{ __html: srvList[0].descripcion }} />}
                            <span className="block mt-1.5">{t.pagoServicioContacto}{" "}
                              <b>{psc2.tiempo_contacto_valor} {t[`pagoUnidad${(psc2.tiempo_contacto_unidad || "horas").charAt(0).toUpperCase()}${(psc2.tiempo_contacto_unidad || "horas").slice(1)}`] || psc2.tiempo_contacto_unidad}</b>.</span>
                          </p>
                        ) : (
                          <>
                            <p className="text-sm text-slate-800">{t.pagoServicioIntroMulti} <b>{t.pagoServicioMarca}</b> {t.pagoServicioSuma} <b>{psc2.tiempo_contacto_valor} {t[`pagoUnidad${(psc2.tiempo_contacto_unidad || "horas").charAt(0).toUpperCase()}${(psc2.tiempo_contacto_unidad || "horas").slice(1)}`] || psc2.tiempo_contacto_unidad}</b> {t.pagoServicioProcesar}</p>
                          </>
                        )}
                      </div>
                    )}

                    {/* Mensaje personalizado cuando el viajero responde NO — se muestra debajo de los botones */}

                    <div data-field="pago_requiere">
                      <label className="block text-[13px] font-bold text-slate-600 mb-1.5">{srvList.length === 1 ? t.pagoRequiereSing : t.pagoRequiereMulti} <span className="text-red-400">*</span></label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[
                          { v: true, l: cfgBotonSi || (srvList.length === 1 ? t.pagoSiSing : t.pagoSiMulti) },
                          { v: false, l: cfgBotonNo || t.pagoNo },
                        ].map((op) => (
                          <label key={String(op.v)} className={`flex items-center gap-3 cursor-pointer rounded-xl border px-4 py-3 transition-all ${pagoSrv.requiere === op.v ? "border-[color:var(--pv-base)] bg-[color:var(--pv-ring)]" : "border-slate-200 bg-slate-50/60 hover:border-slate-300"}`}>
                            <input type="radio" name="pago_requiere" className="w-4 h-4" style={{ accentColor: accentColor }}
                              checked={pagoSrv.requiere === op.v}
                              onChange={() => { setPagoSrv((s) => ({ ...s, requiere: op.v, servicios_seleccionados: op.v ? s.servicios_seleccionados : [] })); setPagoErrors({}); }}
                              data-testid={`pago-requiere-${op.v ? "si" : "no"}`} />
                            <span className="text-sm text-slate-700 font-medium">{op.l}</span>
                          </label>
                        ))}
                      </div>
                      {pagoErrors.requiere && <p className="text-xs text-red-500 mt-1 font-semibold">{pagoErrors.requiere}</p>}
                    </div>

                    {pagoSrv.requiere === false && cfgTextoNo && (
                      <div className="mt-3 rounded-xl p-4 border-2 border-dashed pv-rich text-sm text-slate-700" style={{ borderColor: accentColor, background: `${accentColor}0d` }} data-testid="pago-texto-no" dangerouslySetInnerHTML={{ __html: cfgTextoNo }} />
                    )}

                    {pagoSrv.requiere === true && multi && (
                      <div data-field="pago_servicios">
                        <label className="block text-[13px] font-bold text-slate-600 mb-1.5">{t.pagoSeleccionaLbl} <span className="text-red-400">*</span></label>
                        <div className="space-y-2">
                          {srvList.map((s) => {
                            const on = selectedSet.has(s.id);
                            return (
                              <label key={s.id} className={`flex items-start gap-3 cursor-pointer rounded-xl border px-4 py-3 transition-all ${on ? "border-[color:var(--pv-base)] bg-[color:var(--pv-ring)]" : "border-slate-200 bg-slate-50/60 hover:border-slate-300"}`}>
                                <input type="checkbox" className="w-4 h-4 mt-0.5" style={{ accentColor: accentColor }}
                                  checked={on}
                                  onChange={() => toggleServicio(s.id)}
                                  data-testid={`pago-srv-${s.id}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline justify-between gap-3">
                                    <span className="text-sm font-semibold text-slate-800">{s.nombre}</span>
                                    <span className="text-sm font-bold flex-shrink-0" style={{ color: accentColor }}>${Number(s.costo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</span>
                                  </div>
                                  {s.descripcion && <div className="text-xs text-slate-500 mt-0.5 pv-rich" dangerouslySetInnerHTML={{ __html: s.descripcion }} />}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        {selected.length > 0 && (
                          <div className="mt-3 rounded-xl p-3 flex items-center justify-between text-white font-bold" style={{ background: `linear-gradient(135deg, ${accentColor}, ${shade(accentColor, -0.2)})` }} data-testid="pago-total">
                            <span className="text-sm">{t.pagoTotalLbl} ({selected.length} {selected.length === 1 ? t.pagoServicioWord : t.pagoServiciosWord}):</span>
                            <span className="text-lg">${Number(total).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</span>
                          </div>
                        )}
                        {pagoErrors.servicios && <p className="text-xs text-red-500 mt-1 font-semibold">{pagoErrors.servicios}</p>}
                      </div>
                    )}

                    {pagoSrv.requiere === true && (
                      <div data-field="pago_medios">
                        <label className="block text-[13px] font-bold text-slate-600 mb-1.5">{t.pagoMedioLbl} <span className="text-red-400">*</span></label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            { v: "correo", l: t.pagoMedioEmail, icon: "📧" },
                            { v: "whatsapp", l: t.pagoMedioWA, icon: "💬" },
                          ].map((m) => {
                            const on = pagoSrv.medios.includes(m.v);
                            return (
                              <label key={m.v} className={`flex items-center gap-3 cursor-pointer rounded-xl border px-4 py-3 transition-all ${on ? "border-[color:var(--pv-base)] bg-[color:var(--pv-ring)]" : "border-slate-200 bg-slate-50/60 hover:border-slate-300"}`}>
                                <input type="checkbox" className="w-4 h-4" style={{ accentColor: accentColor }}
                                  checked={on}
                                  onChange={(e) => {
                                    setPagoSrv((s) => ({
                                      ...s,
                                      medios: e.target.checked ? [...s.medios, m.v] : s.medios.filter((x) => x !== m.v),
                                    }));
                                  }}
                                  data-testid={`pago-medio-${m.v}`} />
                                <span className="text-sm text-slate-700 font-medium">{m.icon} {m.l}</span>
                              </label>
                            );
                          })}
                        </div>
                        {pagoErrors.medios && <p className="text-xs text-red-500 mt-1 font-semibold">{pagoErrors.medios}</p>}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            const s = item.data;
            const isCollapsed = !!collapsed[s.key];
            const filled = s.fields.filter(isFilled).length;
          return (
            <div key={s.key} className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-5 overflow-hidden pv-fade transition-shadow duration-300 hover:shadow-md"
              style={{ animationDelay: `${Math.min(si * 70, 400)}ms` }} data-testid={`seccion-${s.key}`}>
              <button type="button" onClick={() => toggleSection(s.key)}
                className="w-full px-5 sm:px-6 py-4 flex items-center gap-3.5 text-left cursor-pointer"
                style={{ background: `linear-gradient(90deg, ${brandColor}08, transparent 60%)`, borderBottom: isCollapsed ? "none" : "1px solid #f8fafc" }}
                data-testid={`seccion-toggle-${s.key}`}>
                <span className="w-8 h-8 rounded-xl text-white text-sm font-extrabold flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: brandColor, boxShadow: `0 4px 10px -3px ${brandColor}66` }}>{secNum}</span>
                <h2 className="font-extrabold text-slate-800 text-[15px] sm:text-base flex-1" style={{ fontFamily: "'Mulish', sans-serif" }}>{s[lang] || s.es}</h2>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                  style={filled === s.fields.length ? { background: "#dcfce7", color: "#15803d" } : { background: `${brandColor}12`, color: brandColor }}>
                  {filled}/{s.fields.length}
                </span>
                <svg className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${isCollapsed ? "" : "rotate-180"}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
              </button>
              {!isCollapsed && (
              <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5" onFocusCapture={() => focusSection(si)}>
                {s.fields.map((f) => {
                  const full = ["textarea", "file", "phone", "check"].includes(f.t);
                  const label = f[lang] || f.es || f.col;
                  return (
                    <div key={f.k} className={full ? "sm:col-span-2" : ""} data-field={f.k}>
                      {f.t !== "check" && (
                        <label className="block text-[13px] font-bold text-slate-600 mb-1.5">
                          {label} {f.req && <span className="text-red-400">*</span>}
                        </label>
                      )}
                      {["text", "alnum", "email"].includes(f.t) && (
                        <input className={inputCls} type={f.t === "email" ? "email" : "text"} value={values[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`campo-${f.k}`} />
                      )}
                      {f.t === "date" && <input className={inputCls} type="date" value={values[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`campo-${f.k}`} />}
                      {f.t === "time" && <input className={inputCls} type="time" value={values[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`campo-${f.k}`} />}
                      {f.t === "select" && (
                        <select className={inputCls} value={values[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`campo-${f.k}`}>
                          <option value="">—</option>
                          {(f.opts || []).map((o) => <option key={o.v} value={o.v}>{o[lang] || o.es || o.v}</option>)}
                        </select>
                      )}
                      {f.t === "textarea" && <textarea className={inputCls} rows={3} value={values[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`campo-${f.k}`} />}
                      {f.t === "phone" && <PhoneField value={values[f.k]} onChange={(v) => set(f.k, v)} lang={lang} testid={`campo-${f.k}`} />}
                      {f.t === "file" && <FileField file={files[f.k]} onFile={(file) => { setFiles((s2) => ({ ...s2, [f.k]: file })); setErrors((e) => ({ ...e, [f.k]: null })); }} lang={lang} testid={`campo-${f.k}`} />}
                      {f.t === "check" && (
                        <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-3 transition-colors hover:bg-slate-50">
                          <input type="checkbox" className="mt-0.5 w-4 h-4 flex-shrink-0" style={{ accentColor: accentColor }} checked={values[f.k] === "Sí"} onChange={(e) => set(f.k, e.target.checked ? "Sí" : "No")} data-testid={`campo-${f.k}`} />
                          <span className="text-sm text-slate-600 leading-snug">
                            {label} {f.req && <span className="text-red-400">*</span>}
                            {f.link && <> — <a href={cfg.aviso_privacidad_url} target="_blank" rel="noreferrer" className="underline font-semibold" style={{ color: brandColor }}>{t.aviso}</a></>}
                          </span>
                        </label>
                      )}
                      {errors[f.k] && <p className="text-xs text-red-500 mt-1 font-semibold" data-testid={`error-${f.k}`}>{errors[f.k]}</p>}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          );
          });
        })()}

        <button onClick={submit} disabled={submitting}
          className="w-full py-4 rounded-2xl text-white font-extrabold text-lg disabled:opacity-60 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${shade(accentColor, 0.12)}, ${accentColor})`, boxShadow: `0 14px 30px -10px ${accentColor}99` }}
          data-testid="submit-registro">
          {submitting ? t.enviando : (<>{t.enviar} <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12l-7.5 7.5M21 12H3" /></svg></>)}
        </button>
      </div>

      <PDFooter headerColor={headerColor} lang={lang} />
    </div>
  );
}
